import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ruler, Sparkles, CircleDollarSign } from "lucide-react";
import { FITTING_STEPS, IMAGES } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

export default function Fitting() {
  const { settings } = useSettings();
  const IMG = { ...IMAGES, ...(settings.images || {}) };
  const fitting = (settings.services || []).find((s) => s.key === "fitting");

  return (
    <div data-testid="page-fitting">
      {/* Hero */}
      <section className="px-4 pt-12 pb-10 sm:px-8 md:px-12 md:pt-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-[1fr_1fr]">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
              {fitting?.category || "Fit Service & Comfort Consult"}
            </p>
            <h1 className="mt-3 font-display text-5xl font-black leading-[0.95] tracking-tight text-leaf-900 sm:text-6xl md:text-7xl">
              A bike that fits <span className="marker-underline italic text-leaf-700">feels like flying.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-leaf-900/80">
              Bring your bike into our studio to learn about products and
              positions that take your cycling journey to the next level. We
              tailor our suggestions for you specifically and have experience
              with most types of bicycles and common ailments.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact#booking"
                data-testid="fitting-cta-book"
                className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
              >
                Request a Fitting <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_20px_60px_rgba(21,128,61,0.18)]">
            <img
              src={IMG.fitting}
              alt="A dialed-in bike out on the trail"
              className="h-[360px] w-full object-cover sm:h-[480px]"
            />
          </div>
        </div>
      </section>

      {/* Fit tiers */}
      <section className="px-4 py-14 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
            Choose your fit
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {(fitting?.items || []).map((tier, i) => (
              <article
                key={tier.name}
                data-testid={`fit-tier-${tier.name.toLowerCase()}`}
                className={`relative rounded-3xl border-2 p-7 transition-all hover:-translate-y-1 hover:shadow-xl ${
                  i === (fitting.items.length - 1)
                    ? "border-leaf-600 bg-leaf-900 text-white"
                    : "border-leaf-200 bg-white"
                }`}
              >
                <span
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${
                    i === (fitting.items.length - 1)
                      ? "bg-sun-400 text-leaf-900"
                      : "bg-leaf-100 text-leaf-700"
                  }`}
                >
                  <Ruler className="h-6 w-6" />
                </span>
                <h3
                  className={`mt-4 font-display text-3xl font-bold ${
                    i === (fitting.items.length - 1) ? "text-white" : "text-leaf-900"
                  }`}
                >
                  {tier.name}
                </h3>
                <p
                  className={`mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-base font-black ${
                    i === (fitting.items.length - 1)
                      ? "bg-sun-400 text-leaf-900"
                      : "bg-sun-100 text-leaf-900"
                  }`}
                >
                  <CircleDollarSign className="h-4 w-4" />
                  {tier.price}
                </p>
                {tier.note && (
                  <p
                    className={`mt-4 ${
                      i === (fitting.items.length - 1)
                        ? "text-leaf-50/85"
                        : "text-leaf-900/75"
                    }`}
                  >
                    {tier.note}
                  </p>
                )}
                <Link
                  to="/contact#booking"
                  data-testid={`fit-tier-cta-${tier.name.toLowerCase()}`}
                  className={`mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all hover:-translate-y-0.5 ${
                    i === (fitting.items.length - 1)
                      ? "bg-sun-400 text-leaf-900 hover:bg-sun-500"
                      : "bg-leaf-600 text-white hover:bg-leaf-700"
                  }`}
                >
                  Request {tier.name} <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline / process */}
      <section className="bg-leaf-50 px-4 py-16 sm:px-8 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
              The process
            </p>
            <h2 className="mt-2 font-display text-4xl font-bold text-leaf-900 sm:text-5xl">
              What a fitting looks like
            </h2>
          </div>

          <ol className="relative space-y-6 border-l-2 border-dashed border-leaf-300 pl-6 sm:pl-10">
            {FITTING_STEPS.map((s, i) => (
              <li
                key={s.title}
                data-testid={`fitting-step-${i + 1}`}
                className="relative"
              >
                <span className="absolute -left-[34px] sm:-left-[50px] flex h-10 w-10 items-center justify-center rounded-full bg-leaf-600 text-sm font-black text-white shadow-lg">
                  {i + 1}
                </span>
                <div className="rounded-3xl border-2 border-leaf-200 bg-white p-6">
                  <h3 className="font-display text-xl font-bold text-leaf-900 sm:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-leaf-900/75">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16 sm:px-8 md:px-12">
        <div
          data-testid="fitting-cta-band"
          className="mx-auto max-w-5xl rounded-[2.5rem] bg-leaf-900 p-8 text-center text-white sm:p-14"
        >
          <Sparkles className="mx-auto h-8 w-8 text-sun-300" />
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">
            Ready to ride how you were meant to?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-leaf-50/85">
            Book a fitting in minutes. Bring your bike (or let us help you pick
            one), and we'll take it from there.
          </p>
          <Link
            to="/contact#booking"
            data-testid="fitting-band-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-sun-400 px-7 py-3.5 text-base font-bold text-leaf-900 transition-all hover:-translate-y-0.5 hover:bg-sun-500"
          >
            Request a Fitting <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
